import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const TowingService = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my towing service business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps towing service businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for towing service businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most towing service businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my towing service business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like towing service. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Towing Service - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Towing Service CRM", url: "/industries/towing-service" }
  ];

  const industryData = {
    name: 'Towing Service',
    icon: '🚚',
    title: 'Never Miss Another Emergency Tow Call & Dominate Roadside Assistance',
    subtitle: 'Capture breakdown calls 24/7, provide instant ETA estimates, and automate dispatch to maximize your fleet utilization',
    
    // Problem Section - Challenges they face
    challenges: [
      'Missing breakdown calls because drivers are already on jobs or out of coverage',
      'Customers call 4-5 towing companies - first to answer wins the job',
      'No system to provide instant ETA or pricing estimates to stranded drivers',
      'Manual dispatch coordination wastes time and causes driver confusion',
      'Police impound contracts available but no professional system to manage them',
      'Drivers can\'t update job status in real-time while on the road',
      'Completed hundreds of tows but barely any Google reviews',
      'Seasonal slowdowns (winter busy, summer quiet) cause unpredictable income'
    ],
    
    // Solution Section - How we solve these
    solutions: [
      '24/7 emergency breakdown request form with GPS location capture provides instant SMS acknowledgment so drivers know help is coming',
      'Automated ETA calculator shows customers estimated arrival time based on driver availability and location',
      'Smart dispatch system routes jobs to nearest available driver with automatic SMS job details and location',
      'Driver mobile app allows real-time status updates (en route → on scene → vehicle loaded → delivered)',
      'Corporate roadside assistance packages target fleet managers, rental companies, and insurance providers for recurring contracts',
      'Automated payment collection with digital invoicing reduces cash handling and speeds up payment',
      'Review automation requests 5-star Google reviews after safe vehicle delivery with driver photo recognition'
    ],
    
    // Features Section - What's included in snapshot
    features: [
      '24/7 Emergency Breakdown Request Form with GPS Location',
      'Instant ETA & Pricing Estimate Calculator',
      'Smart Driver Dispatch System (auto-route to nearest driver)',
      'Driver Mobile App (real-time job status updates)',
      'Police Impound Job Management Portal',
      'Corporate Fleet Roadside Assistance Packages',
      'Automated Customer SMS Updates (ETA, on scene, in transit)',
      'Digital Job Sheet & Photo Documentation',
      'Automated Payment Collection & Digital Invoicing',
      'Google Review Request Automation',
      'Multi-Vehicle Fleet Management Dashboard',
      'Coverage Area Mapper & Service Zone Management',
      'Insurance Company Integration (for approved provider work)',
      'Driver Performance Tracking & Job History'
    ],
    
    // Results Section - Success metrics
    results: [
      '92% emergency call response rate',
      '11 minute average response time to breakdowns',
      '4.8x more corporate contracts secured'
    ],
    
    // SEO Section
    metaTitle: 'Towing Service Growth Platform | Never Miss Emergency Breakdown Calls',
    metaDescription: '24/7 breakdown call capture, instant ETA estimates, smart driver dispatch, and corporate roadside assistance management for towing companies.',
    
    // Reviews Section
    reviews: [
      {
        author: 'Ryan M.',
        business: '24/7 Emergency Towing',
        location: 'Leeds',
        rating: 5,
        text: 'We were missing 40% of breakdown calls because drivers couldn\'t answer while towing. The 24/7 request form with GPS location capture changed everything. Instant SMS acknowledgment with ETA keeps customers calm. Our emergency call capture went from 60% to 94% in just 2 months. Revenue up £9,200 per month.',
        verified: true
      },
      {
        author: 'Michelle G.',
        business: 'Roadside Recovery Services',
        location: 'Manchester',
        rating: 5,
        text: 'The smart dispatch system routes jobs to our nearest available driver automatically. No more manual coordination over walkie-talkies. Drivers get SMS with job details, customer location, and vehicle info instantly. Our average response time dropped from 34 minutes to 11 minutes. Customers love the real-time ETA updates.',
        verified: true
      },
      {
        author: 'Andrew S.',
        business: 'Commercial Towing & Recovery',
        location: 'Birmingham',
        rating: 5,
        text: 'The corporate roadside assistance packages opened up a whole new revenue stream. We now have contracts with 3 rental car companies and 2 insurance providers for their roadside assistance callouts. Worth £126K+ annually in recurring work. The police impound management portal is great too - tracking every impound job properly.',
        verified: true
      },
      {
        author: 'Kelly P.',
        business: 'Auto Recovery & Towing',
        location: 'Glasgow',
        rating: 5,
        text: 'The driver mobile app is brilliant. Drivers update job status in real-time (en route, on scene, vehicle loaded) and customers get automatic SMS updates at every stage. No more "where\'s the tow truck?" calls. Our Google reviews went from 7 to 61 thanks to the automated review requests after safe delivery. Winning way more jobs.',
        verified: true
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Towing Service UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for towing service businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for towing service UK, towing service business software, towing service customer management, towing service lead generation UK"
        url="/industries/towing-service"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default TowingService;